import { AlarmClock, Briefcase } from 'assets/icons/revamp';
import AddFreeTimeHabitButton from 'components/setting-generator-revamp/tabs-body-steps-revamp/routines/tab-body-header-revamp/tabBody-header-widgets/AddFreeTimeHabitButton';
import { ATTRIB } from 'constants/test';
import { PropsWithChildren, useEffect } from 'react';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { timingSelector } from 'store/reducer/setting/selectors';
import {
  setHighlightShutdownTime,
  updateRoutineTimeValidation,
  updateShutdownTime,
  updateStartupTime
} from 'store/reducer/setting/slice';
import {
  shouldAllowAddFreeTimeHabit,
  shouldShowGlobalCutoffTime
} from 'utils/settings.util';
import { validateRoutineTimes } from 'utils/validators';
import { SkeletonTiming } from '../skeleton-routine';
import GlobalCutoffTimeRevamp from './GlobalCutoffTimeRevamp';
import TimeInputField from './TimeInputField';
import { TIME_WHEEL_PICKER_REMOUNT_KEY } from 'constants/time-wheel-picker';
import COLOR from 'constants/color';

const Wrapper = ({ children }: PropsWithChildren) => (
  <div
    className='w-full flex flex-col ~gap-5/10 overflow-y-auto scrollbar-thin relative'
    data-testid={ATTRIB.DATA_TESTID.TIME_PAGE_WRAPPER}>
    {children}
  </div>
);

const Timing = () => {
  const dispatch = useAppDispatch();

  const {
    startup_time,
    shutdown_time,
    globalCutoffTime,
    evening_activities,
    userFocusModes,
    routineTimeValidation,
    highlightShutdownTime,
    isSettingLoading,
    flags,
    platform
  } = useAppSelectorShallow(timingSelector);

  const showGlobalCutoffTime = shouldShowGlobalCutoffTime(flags, platform);

  useEffect(() => {
    const validationResult = validateRoutineTimes(
      startup_time,
      shutdown_time,
      showGlobalCutoffTime ? globalCutoffTime : undefined
    );
    dispatch(updateRoutineTimeValidation(validationResult));
  }, [
    startup_time,
    shutdown_time,
    globalCutoffTime,
    showGlobalCutoffTime,
    dispatch
  ]);

  const canAddFreeTimeHabit = shouldAllowAddFreeTimeHabit({
    flags,
    globalCutoffTime,
    shutdown_time,
    evening_activities,
    userFocusModes,
    platform
  });

  if (isSettingLoading) {
    return (
      <Wrapper>
        <SkeletonTiming />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {canAddFreeTimeHabit ? <AddFreeTimeHabitButton /> : null}

      <div className='flex flex-col ~gap-4/6'>
        <TimeInputField
          icon={<AlarmClock />}
          labelKey='what_time_do_you_want_to_wake_up'
          timeValue={startup_time}
          routineTimeValidation={routineTimeValidation}
          updateAction={updateStartupTime}
          validationKey='startupTime'
          defaultTime='06:30'
          dataTestId={ATTRIB.DATA_TESTID.STARTUP_TIME_WRAPPER}
          key={`startup-${TIME_WHEEL_PICKER_REMOUNT_KEY.READY}`}
        />
        <TimeInputField
          icon={
            <Briefcase
              fill={COLOR.PRIMARY.LIGHT_500}
              fillDarkMode={COLOR.PRIMARY.LIGHT_500}
            />
          }
          labelKey='what_time_do_you_finish_work'
          timeValue={shutdown_time}
          routineTimeValidation={routineTimeValidation}
          updateAction={updateShutdownTime}
          validationKey='shutdownTime'
          defaultTime='23:00'
          shouldHighlight={highlightShutdownTime}
          onHighlightComplete={() => dispatch(setHighlightShutdownTime(false))}
          dataTestId={ATTRIB.DATA_TESTID.SHUTDOWN_TIME_WRAPPER}
          key={`shutdown-${TIME_WHEEL_PICKER_REMOUNT_KEY.READY}`}
        />
        {showGlobalCutoffTime && <GlobalCutoffTimeRevamp />}
      </div>
    </Wrapper>
  );
};

export default Timing;
